/**
 * Dashboard Redux Slice
 * Manages dashboard analytics and summary state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DashboardSummary,
  WorkspaceDashboardSummary,
} from '../../api/types/DashboardTypes';
import { dashboardApi } from '../../api/endpoints/dashboardApi';

// ==================== State Interface ====================

interface DashboardState {
  // User dashboard
  userDashboard: DashboardSummary | null;
  userDashboardLoading: boolean;
  userDashboardError: string | null;

  // Workspace dashboards (keyed by workspaceId)
  workspaceDashboards: { [workspaceId: string]: WorkspaceDashboardSummary };
  workspaceDashboardLoading: boolean;
  workspaceDashboardError: string | null;

  // Last refresh timestamp
  lastRefresh: string | null;
}

const initialState: DashboardState = {
  userDashboard: null,
  userDashboardLoading: false,
  userDashboardError: null,
  workspaceDashboards: {},
  workspaceDashboardLoading: false,
  workspaceDashboardError: null,
  lastRefresh: null,
};

// ==================== Async Thunks ====================

/**
 * Fetch user dashboard summary
 */
export const fetchUserDashboardSummary = createAsyncThunk(
  'dashboard/fetchUserDashboardSummary',
  async () => {
    const summary = await dashboardApi.getUserDashboardSummary();
    return summary;
  },
);

/**
 * Fetch workspace dashboard summary
 */
export const fetchWorkspaceDashboardSummary = createAsyncThunk(
  'dashboard/fetchWorkspaceDashboardSummary',
  async (workspaceId: string) => {
    const summary = await dashboardApi.getWorkspaceDashboardSummary(workspaceId);
    return { workspaceId, summary };
  },
);

// ==================== Slice ====================

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Clear dashboard data
    clearDashboard(state) {
      state.userDashboard = null;
      state.workspaceDashboards = {};
      state.lastRefresh = null;
    },

    // Clear errors
    clearErrors(state) {
      state.userDashboardError = null;
      state.workspaceDashboardError = null;
    },

    // Clear specific workspace dashboard
    clearWorkspaceDashboard(state, action) {
      const workspaceId = action.payload;
      delete state.workspaceDashboards[workspaceId];
    },
  },
  extraReducers: (builder) => {
    // ==================== Fetch User Dashboard Summary ====================
    builder
      .addCase(fetchUserDashboardSummary.pending, (state) => {
        state.userDashboardLoading = true;
        state.userDashboardError = null;
      })
      .addCase(fetchUserDashboardSummary.fulfilled, (state, action) => {
        state.userDashboardLoading = false;
        state.userDashboard = action.payload;
        state.lastRefresh = new Date().toISOString();
      })
      .addCase(fetchUserDashboardSummary.rejected, (state, action) => {
        state.userDashboardLoading = false;
        state.userDashboardError =
          action.error.message || 'Failed to fetch dashboard summary';
      });

    // ==================== Fetch Workspace Dashboard Summary ====================
    builder
      .addCase(fetchWorkspaceDashboardSummary.pending, (state) => {
        state.workspaceDashboardLoading = true;
        state.workspaceDashboardError = null;
      })
      .addCase(fetchWorkspaceDashboardSummary.fulfilled, (state, action) => {
        state.workspaceDashboardLoading = false;
        const { workspaceId, summary } = action.payload;
        state.workspaceDashboards[workspaceId] = summary;
        state.lastRefresh = new Date().toISOString();
      })
      .addCase(fetchWorkspaceDashboardSummary.rejected, (state, action) => {
        state.workspaceDashboardLoading = false;
        state.workspaceDashboardError =
          action.error.message || 'Failed to fetch workspace dashboard summary';
      });
  },
});

// ==================== Exports ====================

export const { clearDashboard, clearErrors, clearWorkspaceDashboard } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
